import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import React, { createContext, useState, useEffect, useRef } from 'react';

import {
  ACCESS_TOKEN_COOKIE_NAME,
  ADMIN_USER_COOKIE_NAME,
  MD_AUTH_LOCAL_STORAGE_KEY,
} from '~/common/constants/auth';
import { MobileNumberDialog } from '~/components/AuthenticationProvider/MobileNumberDialog/MobileNumberDialog';
import { AuthApi } from '~/services/auth/authApi';
import { RestApi } from '~/services/restApi';
import { UserApi } from '~/services/user/UserApi';
import { setLocalStorage } from '~/utils/localStorage';

import { AuthDialog } from './AuthDialog/AuthDialog';
import { EmailLoginDialog } from './EmailLoginDialog/EmailLoginDialog';
import { EmailSignUpDialog } from './EmailSignUpDialog/EmailSignUpDialog';
import { SocialSignUpDialog } from './SocialSignUpDialog/SocialSignUpDialog';
import { getGrantTypeFromAuthMethod } from './utils';
import { identify, trackEvent } from '../analytics/analytics';
import {
  LOGGED_IN_FE_EVENT_NAME,
  LOGIN_MODAL_VIEWED_EVENT_NAME,
  SIGNED_UP_FE_EVENT_NAME,
  USER_LOG_IN_COMPLETED_EVENT_NAME,
  USER_SIGNUP_COMPLETED_EVENT_NAME,
} from '../analytics/constants';

import type { AuthMethods } from './constants';
import type {
  OnLoginSuccessHandler,
  OnOpenAuthDialogHandler,
  OnSignUpSuccessHandler,
  OnSocialSignUpHandler,
} from './types';
import type { ReactElement, ReactNode } from 'react';
import type { UserDetails } from '~/common/types/user';
import type {
  GetAuthTokenRESTRequest,
  LoginSocialNewUserRestResponse,
} from '~/services/auth/types';

type Props = {
  children: ReactNode;
  route: string;
  userDetails: UserDetails | null;
};

export type Context = {
  getUserDetails: () => Promise<void>;
  isAuthDialogOpen: boolean;
  isEmailLoginDialogOpen: boolean;
  isEmailSignUpDialogOpen: boolean;
  isMobileNumberDialogOpen: boolean;
  isSocialSignUpDialogOpen: boolean;
  isUserDetailsLoading: boolean;
  isUserLoggedIn: boolean | null;
  isUserSitter: boolean;
  loginAsAdmin: (impersonationToken: string, onSuccess: () => void) => Promise<void>;
  openAuthDialog: OnOpenAuthDialogHandler;
  setEmail: (email: string) => void;
  setEmailLoginDialogVisible: (isVisible: boolean) => void;
  setIsAuthDialogOpen: (isOpen: boolean) => void;
  setIsEmailSignUpDialogOpen: (isOpen: boolean) => void;
  setIsMobileNumberDialogOpen: (isOpen: boolean) => void;
  setIsSocialSignUpDialogOpen: (isOpen: boolean) => void;
  setIsUserLoggedIn(isLoggedIn: boolean): void;
  user: UserDetails | null;
};

export const AuthenticationContext = createContext<Context>({
  isAuthDialogOpen: false,
  openAuthDialog: () => {},
  setEmail: () => {},
  setEmailLoginDialogVisible: () => {},
  isUserDetailsLoading: true,
  isUserSitter: false,
  user: null,
  getUserDetails: async () => {},
  isUserLoggedIn: false,
  setIsUserLoggedIn: () => {},
  setIsMobileNumberDialogOpen: () => {},
  setIsAuthDialogOpen: () => {},
  isMobileNumberDialogOpen: false,
  isSocialSignUpDialogOpen: false,
  setIsSocialSignUpDialogOpen: () => {},
  isEmailLoginDialogOpen: false,
  setIsEmailSignUpDialogOpen: () => {},
  isEmailSignUpDialogOpen: false,
  loginAsAdmin: async () => {},
});

export const AuthenticationProvider = ({ children, userDetails }: Props): ReactElement => {
  const [title, setTitle] = useState('');
  const [promoQueryParam, setPromoQueryParam] = useState('');
  const [onLoginSuccessRedirectUrl, setOnLoginSuccessRedirectUrl] = useState('');
  const [socialSignUpData, setSocialSignUpData] = useState<
    (LoginSocialNewUserRestResponse & { authMethod: AuthMethods }) | null
  >(null);

  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [isEmailSignUpDialogOpen, setIsEmailSignUpDialogOpen] = useState(false);
  const [isMobileNumberDialogOpen, setIsMobileNumberDialogOpen] = useState(false);
  const [isSocialSignUpDialogOpen, setIsSocialSignUpDialogOpen] = useState<boolean>(false);
  const [isEmailLoginDialogOpen, setEmailLoginDialogVisible] = useState<boolean>(false);

  const [email, setEmail] = useState<string>('');
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean | null>(null);
  const [user, setUser] = useState<UserDetails | null>(userDetails);

  const loginSuccessCallbackRef = useRef<
    ((userData: { email: string; userId: number }) => void) | null
  >(null);
  const signupSuccessCallbackRef = useRef<
    ((userData: { email: string; userId: number }) => void) | null
  >(null);
  const validatePhoneSuccessCallbackRef = useRef<((userID: number) => void) | undefined>();

  const router = useRouter();

  useEffect(() => {
    // initialize isUserLoggedIn
    const isAuthCookiesProvided = !!Cookies.get(ACCESS_TOKEN_COOKIE_NAME);
    setIsUserLoggedIn(isAuthCookiesProvided);
  }, []);

  useEffect(() => {
    if (user) {
      identify(user.id, { isSitter: user.isSitter });
    }
  }, [user]);

  const getUserDetails = async (): Promise<void> => {
    const userRefresh = await UserApi.getUserDetails();
    setUser(userRefresh);
  };

  const openAuthDialog = (
    data: {
      queryParam?: string;
      shouldValidatePhone?: boolean;
      successRedirectUrl?: string;
      title: string;
    },
    loginSuccessCallback?: (userData: { email: string; userId: number }) => void,
    signupSuccessCallback?: (userData: { email: string; userId: number }) => void,
    validatePhoneSuccessCallback?: (userID: number) => void
  ): void => {
    const { title: newTitle, successRedirectUrl, queryParam, shouldValidatePhone } = data;

    trackEvent(LOGIN_MODAL_VIEWED_EVENT_NAME);
    setIsAuthDialogOpen(true);
    setTitle(newTitle);
    setOnLoginSuccessRedirectUrl(successRedirectUrl ?? '');
    setPromoQueryParam(queryParam ?? '');
    loginSuccessCallbackRef.current = loginSuccessCallback ?? null;
    signupSuccessCallbackRef.current = signupSuccessCallback ?? null;
    if (shouldValidatePhone) {
      validatePhoneSuccessCallbackRef.current = validatePhoneSuccessCallback;
    }
  };

  const onLoginSuccess: OnLoginSuccessHandler = async (data, userData, authMethod) => {
    await identify(userData.userId, {});

    Cookies.set(ACCESS_TOKEN_COOKIE_NAME, JSON.stringify({ tokens: data }) ?? '');
    setLocalStorage(MD_AUTH_LOCAL_STORAGE_KEY, { ...data });
    RestApi.setAuthorizationHeader({ authorization: `Bearer ${data.access_token}` });
    getUserDetails();
    setIsUserLoggedIn(true);

    trackEvent(USER_LOG_IN_COMPLETED_EVENT_NAME, {
      type: authMethod,
      user_id: userData.userId,
      email: userData.email,
      user_channel: 'website',
      grant_type: getGrantTypeFromAuthMethod(authMethod),
    });

    // This is added for maintain legacy events in Mixpanel
    trackEvent(LOGGED_IN_FE_EVENT_NAME, {
      type: authMethod,
      id: userData.userId,
      email: userData.email,
      grant_type: getGrantTypeFromAuthMethod(authMethod),
      sms_verification_required: false,
    });

    if (loginSuccessCallbackRef.current) {
      setTimeout(() => {
        loginSuccessCallbackRef.current?.(userData);
      }, 500);
    }

    if (onLoginSuccessRedirectUrl) {
      setTimeout(() => {
        router.push(onLoginSuccessRedirectUrl);
      }, 500);
    }
  };

  const onSocialSignUp: OnSocialSignUpHandler = (data, authMethod) => {
    setSocialSignUpData({ ...data, authMethod });
    setIsSocialSignUpDialogOpen(true);
  };

  const onSignUpSuccess: OnSignUpSuccessHandler = async (data, userData, authMethod) => {
    await identify(userData.userId, {});

    trackEvent(USER_SIGNUP_COMPLETED_EVENT_NAME, {
      type: authMethod,
      promo: promoQueryParam,
      user_id: userData.userId,
      user_channel: 'website',
      email: userData.email,
      sms_verification_required: false,
      grant_type: getGrantTypeFromAuthMethod(authMethod),
    });

    trackEvent(SIGNED_UP_FE_EVENT_NAME, {
      type: authMethod,
      promo: promoQueryParam,
      id: userData.userId,
      user_channel: 'website',
      email: userData.email,
      sms_verification_required: false,
      grant_type: getGrantTypeFromAuthMethod(authMethod),
    });

    await onLoginSuccess(data, userData, authMethod);

    if (signupSuccessCallbackRef.current) {
      setTimeout(() => {
        signupSuccessCallbackRef.current?.(userData);
      }, 500);
    }

    if (onLoginSuccessRedirectUrl) {
      setTimeout(() => {
        router.push(onLoginSuccessRedirectUrl);
      }, 500);
    }
  };

  const setAdminUserCookie = (): void => {
    const expires = `${dayjs().add(1, 'day').format('ddd, DD MMM YYYY HH:MM:SS')} GMT`;
    Cookies.set(ADMIN_USER_COOKIE_NAME, '1', { expires: new Date(expires) });
  };

  const loginAsAdmin = async (impersonationToken: string, onSuccess: () => void): Promise<void> => {
    if (!impersonationToken) {
      return;
    }

    const credentials = {
      impersonationToken,
    };

    const data = await AuthApi.getAuthToken(credentials as GetAuthTokenRESTRequest);

    Cookies.set(ACCESS_TOKEN_COOKIE_NAME, JSON.stringify({ tokens: data }) ?? '');
    setLocalStorage(MD_AUTH_LOCAL_STORAGE_KEY, { ...data });
    RestApi.setAuthorizationHeader({ authorization: `Bearer ${data.access_token}` });

    getUserDetails();

    setAdminUserCookie();
    setIsUserLoggedIn(true);

    onSuccess();
  };

  return (
    <AuthenticationContext.Provider
      value={{
        isAuthDialogOpen,
        isSocialSignUpDialogOpen,
        openAuthDialog,
        setEmail,
        setEmailLoginDialogVisible,
        setIsEmailSignUpDialogOpen,
        setIsMobileNumberDialogOpen,
        setIsAuthDialogOpen,
        setIsSocialSignUpDialogOpen,
        isUserDetailsLoading: false,
        isUserSitter: user?.isSitter ?? false,
        user,
        getUserDetails,
        isUserLoggedIn,
        setIsUserLoggedIn,
        isMobileNumberDialogOpen,
        isEmailLoginDialogOpen,
        isEmailSignUpDialogOpen,
        loginAsAdmin,
      }}
    >
      <>
        {children}

        <AuthDialog onLoginSuccess={onLoginSuccess} onSocialSignUp={onSocialSignUp} title={title} />
        <EmailLoginDialog email={email} onLoginSuccess={onLoginSuccess} />
        <EmailSignUpDialog email={email} onSignUpSuccess={onSignUpSuccess} />
        <SocialSignUpDialog onSignUpSuccess={onSignUpSuccess} signUpData={socialSignUpData} />
        <MobileNumberDialog handleValidatePhoneSuccess={validatePhoneSuccessCallbackRef.current} />
      </>
    </AuthenticationContext.Provider>
  );
};
